/**
 * Dashboard — KPI cards + sortable lead table + "email me now" button.
 *
 * Polls the leads list every 8 seconds while any lead is still pending or
 * processing, so newly-uploaded leads update without a manual refresh.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';

import LeadTable from '../components/LeadTable';
import ScoreCard from '../components/ScoreCard';
import CloseRateCard from '../components/CloseRateCard';
import { LeadsAPI, ReportsAPI, errorMessage } from '../services/api';

const FILTERS = [
    { key: 'all',     label: 'All' },
    { key: 'hot',     label: 'Hot' },
    { key: 'pending', label: 'Pending' },
    { key: 'error',   label: 'Error' },
];

export default function Dashboard() {
    const [leads, setLeads]       = useState([]);
    const [quota, setQuota]       = useState(null);
    const [sortBy, setSortBy]     = useState('sales_probability');
    const [order, setOrder]       = useState('desc');
    const [loading, setLoading]   = useState(true);
    const [error, setError]       = useState(null);
    const [emailingNow, setEmailingNow] = useState(false);
    const [filter, setFilter]     = useState('all');

    // flash = { msg: string, isError: boolean } | null
    const [flash, setFlash]                     = useState(null);
    const [flashVisible, setFlashVisible]         = useState(false);
    const [closeRateRefreshKey, setCloseRateRefreshKey] = useState(0);

    function showFlash(msg, isError = false) {
        setFlashVisible(false);
        setFlash({ msg, isError });
    }

    const load = useCallback(async () => {
        try {
            const data = await LeadsAPI.list({ sortBy, order, limit: 200 });
            setLeads(data.leads || []);
            setQuota(data.quota || null);
            setError(null);
        } catch (e) {
            setError(errorMessage(e));
        } finally {
            setLoading(false);
        }
    }, [sortBy, order]);

    useEffect(() => { load(); }, [load]);

    // Auto-refresh while any lead is mid-analysis.
    useEffect(() => {
        const stillProcessing = leads.some(
            (l) => l.analysis_status === 'pending' || l.analysis_status === 'processing',
        );
        if (!stillProcessing) return;
        const id = setInterval(load, 8000);
        return () => clearInterval(id);
    }, [leads, load]);

    // Enter animation: defer opacity/translate change one frame so CSS transition fires.
    useEffect(() => {
        if (!flash) { setFlashVisible(false); return; }
        const raf = requestAnimationFrame(() => setFlashVisible(true));
        return () => cancelAnimationFrame(raf);
    }, [flash]);

    // Auto-clear flash after 4 seconds.
    useEffect(() => {
        if (!flash) return;
        const t = setTimeout(() => setFlash(null), 4000);
        return () => clearTimeout(t);
    }, [flash]);

    function handleSortChange(key) {
        if (sortBy === key) {
            setOrder(order === 'desc' ? 'asc' : 'desc');
        } else {
            setSortBy(key);
            setOrder('desc');
        }
    }

    function handleLeadChange(updated) {
        if (updated._deleted) {
            setLeads((curr) => curr.filter((l) => l.id !== updated.id));
        } else {
            setLeads((curr) => curr.map((l) => (l.id === updated.id ? updated : l)));
        }
        setCloseRateRefreshKey((k) => k + 1);
    }

    async function handleEmailNow() {
        try {
            setEmailingNow(true);
            setFlash(null);
            const res = await ReportsAPI.emailNow();
            showFlash(`Sent to ${res.sent_to} — ${res.count} lead${res.count === 1 ? '' : 's'} included.`);
        } catch (e) {
            showFlash(errorMessage(e), true);
        } finally {
            setEmailingNow(false);
        }
    }

    // Derived KPIs (always from the full unfiltered list).
    const analyzed   = leads.filter((l) => l.analysis_status === 'done');
    const avgSales   = analyzed.length
        ? (analyzed.reduce((s, l) => s + (l.sales_probability || 0), 0) / analyzed.length).toFixed(1)
        : '—';
    const hot        = analyzed.filter((l) => (l.sales_probability || 0) >= 8).length;
    const processing = leads.filter((l) => l.analysis_status === 'pending' || l.analysis_status === 'processing').length;

    // Filter pill counts.
    const filterCounts = {
        all:     leads.length,
        hot:     leads.filter((l) => l.analysis_status === 'done' && (l.sales_probability || 0) >= 8).length,
        pending: leads.filter((l) => l.analysis_status === 'pending' || l.analysis_status === 'processing').length,
        error:   leads.filter((l) => l.analysis_status === 'error').length,
    };

    // Client-side filtered slice passed to LeadTable.
    const filteredLeads = leads.filter((l) => {
        if (filter === 'hot')     return l.analysis_status === 'done' && (l.sales_probability || 0) >= 8;
        if (filter === 'pending') return l.analysis_status === 'pending' || l.analysis_status === 'processing';
        if (filter === 'error')   return l.analysis_status === 'error';
        return true;
    });

    // Context-aware empty state message for the table.
    const activeFilterLabel = FILTERS.find((f) => f.key === filter)?.label ?? filter;
    const emptyMessage = leads.length === 0 || filter === 'all'
        ? undefined  // fall through to LeadTable's built-in default
        : (
            <>
                No leads match the <strong>{activeFilterLabel}</strong> filter —{' '}
                <button
                    onClick={() => setFilter('all')}
                    className="text-brand-600 underline hover:text-brand-700 font-medium"
                >
                    All
                </button>
                {' '}to see all your leads.
            </>
        );

    return (
        <>
            <div className="space-y-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <h1 className="text-2xl font-semibold text-gray-900">Your leads</h1>
                        <p className="text-sm text-gray-500 mt-0.5">
                            {quota
                                ? `${quota.used} / ${quota.limit === Infinity ? '∞' : quota.limit} leads used this month`
                                : 'Loading quota…'}
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <Link
                            to="/upload"
                            className="px-4 py-2 rounded bg-brand-500 text-white font-medium text-sm hover:bg-brand-600"
                        >
                            + New leads
                        </Link>
                        <button
                            onClick={handleEmailNow}
                            disabled={emailingNow}
                            className="px-4 py-2 rounded border border-gray-300 text-gray-700 text-sm hover:bg-gray-100 disabled:opacity-50"
                        >
                            {emailingNow ? 'Sending…' : 'Email me top 5'}
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded p-3">{error}</div>
                )}

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <ScoreCard label="Total leads"     value={leads.length} accent="brand" />
                    <ScoreCard label="Analyzed"        value={analyzed.length} accent="green" />
                    <ScoreCard label="Hot (≥8)"        value={hot} accent="amber" />
                    <ScoreCard label="Avg sales score" value={avgSales} hint="of 10" accent="gray" />
                </div>

                <CloseRateCard refreshKey={closeRateRefreshKey} />

                {processing > 0 && (
                    <div className="bg-blue-50 border border-blue-200 text-blue-800 text-sm rounded p-3">
                        {processing} lead{processing === 1 ? '' : 's'} still being analyzed by Claude — refreshing automatically.
                    </div>
                )}

                {/* Status filter pills */}
                <div className="flex flex-wrap gap-2">
                    {FILTERS.map(({ key, label }) => {
                        const active = filter === key;
                        return (
                            <button
                                key={key}
                                onClick={() => setFilter(key)}
                                className={`px-3 py-1 rounded-full border text-xs font-medium transition-colors ${
                                    active
                                        ? 'bg-brand-50 text-brand-700 border-brand-200'
                                        : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                                }`}
                            >
                                {label} · {filterCounts[key]}
                            </button>
                        );
                    })}
                </div>

                {loading ? (
                    <div className="text-center text-sm text-gray-500 py-12">Loading…</div>
                ) : (
                    <LeadTable
                        leads={filteredLeads}
                        sortBy={sortBy}
                        order={order}
                        onSortChange={handleSortChange}
                        onChange={handleLeadChange}
                        onFlash={(msg) => showFlash(msg)}
                        emptyMessage={emptyMessage}
                    />
                )}
            </div>

            {/* Fixed toast — outside the stacking context so it never shifts layout */}
            {flash && (
                <div
                    className={`fixed top-20 right-4 z-50 flex items-start gap-3 px-4 py-3 rounded-lg shadow-lg border text-sm max-w-sm transition-all duration-200 ease-out ${
                        flashVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-2'
                    } ${
                        flash.isError
                            ? 'bg-red-50 border-red-200 text-red-800'
                            : 'bg-green-50 border-green-200 text-green-800'
                    }`}
                >
                    <span className="flex-1">{flash.msg}</span>
                    <button
                        onClick={() => setFlash(null)}
                        aria-label="Dismiss"
                        className="shrink-0 opacity-50 hover:opacity-100 leading-none text-base font-bold"
                    >
                        ×
                    </button>
                </div>
            )}
        </>
    );
}
